import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSecurityContext } from '@/contexts/MaintenanceContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, RefreshCw, Monitor, Edit, Trash2, Copy, Check, ScanLine, ChevronDown, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Search, X, Layers } from 'lucide-react';
import { CreacionMasivaElementosDialog } from '@/components/inventario/CreacionMasivaElementosDialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BackButton } from '@/components/BackButton';
import { getFieldsForTipo, applyDerived } from '@/lib/inventario/fieldSchemas';
import { DatosTecnicosFields } from '@/components/inventario/DatosTecnicosFields';
import { ElementoDetalleFila } from '@/components/inventario/ElementoDetalleFila';
import { ColumnasElementosPicker } from '@/components/inventario/ColumnasElementosPicker';
import { useElementoColumnPrefs } from '@/hooks/useElementoColumnPrefs';
import { ELEMENTO_COLUMNS } from '@/lib/inventario/elementoColumns';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SearchableCombobox } from '@/components/ui/searchable-combobox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';

import { AnomaliasIndicator } from '@/components/anomalias/AnomaliasIndicator';
import { useAnomalias } from '@/hooks/useAnomalias';

import { QRScannerDialog } from '@/components/inventario/QRScannerDialog';
import { ElementoFotosButton } from '@/components/inventario/ElementoFotosButton';
import { parseExtintorQR } from '@/lib/qrParserUtils';
import { SistemaRevisionActions } from '@/components/sistemas/SistemaRevisionActions';


const ElementosSistema = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  // allowedIds se calcula más abajo cuando ya tenemos los elementos del sistema
  const { 
    elementos, 
    sistemas,
    sistemaActual,
    clienteActual,
    tiposEquipo,
    marcasEquipo,
    modelosEquipo,
    recursosOcultos,
    cargarElementos, 
    crearElemento,
    editarElemento,
    eliminarElemento,
    crearTipoEquipo,
    crearMarcaEquipo,
    crearModeloEquipo,
    isLoading 
  } = useSecurityContext();

  const [dialogAbierto, setDialogAbierto] = useState(false);
  const [creacionMasivaAbierta, setCreacionMasivaAbierta] = useState(false);
  const [elementoEditando, setElementoEditando] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    tipo_equipo: '',
    ubicacion: '',
    fecha_instalacion: '',
    marca: '',
    modelo: '',
    mac_serie: '',
    anio_fabricacion: '',
    observaciones: '',
    estado: 'activo',
    datos_tecnicos: {} as Record<string, any>,
  });
  const [qrDialogAbierto, setQrDialogAbierto] = useState(false);
  const qrJustClosedRef = useRef(false);
  const [filasExpandidas, setFilasExpandidas] = useState<Set<string>>(new Set());

  // Estados para crear nuevos equipos, marcas y modelos si es necesario
  const [nuevoTipoEquipo, setNuevoTipoEquipo] = useState('');
  const [nuevaMarca, setNuevaMarca] = useState('');
  const [nuevoModelo, setNuevoModelo] = useState('');
  


  
  
  // Filtros de tabla
  const [filtroTipo, setFiltroTipo] = useState<string>('');
  const [filtroEstado, setFiltroEstado] = useState<string>('');
  const [filtroAnomalia, setFiltroAnomalia] = useState<string>('');
  const [busquedaGlobal, setBusquedaGlobal] = useState('');
  const [filtroMarca, setFiltroMarca] = useState('');
  const [filtroModelo, setFiltroModelo] = useState('');
  const [filtroZona, setFiltroZona] = useState('');

  // Ordenación de tabla
  const [sortColumn, setSortColumn] = useState<string>('nombre');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const elementosDelSistema = elementos.filter(
    elemento => elemento.sistema_id === sistemaActual?.id
  );

  const tiposDisponibles = Array.from(new Set(elementosDelSistema.map(e => e.tipo).filter(Boolean)));
  const estadosDisponibles = ['activo', 'inactivo', 'mantenimiento', 'averiado'];
  const marcasDisponibles = Array.from(new Set(elementosDelSistema.map(e => e.marca).filter(Boolean)));
  const modelosDisponibles = Array.from(new Set(elementosDelSistema.map(e => e.modelo).filter(Boolean)));
  const zonasDisponibles = Array.from(new Set(elementosDelSistema.map(e => e.zona).filter(Boolean)));

  const { anomaliasElementos, cargarAnomaliasElementos, resolverAnomalia } = useAnomalias();

  // Normalizar el tipo de sistema actual
  const normalizar = (txt: string | null | undefined) =>
    (txt ?? '')
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();

  const sistemaTipoNormalizado = normalizar(sistemaActual?.tipo || '');

  // Filtrar marcas por sistema tipo (excluyendo ocultas)
  const marcasDelSistema = marcasEquipo.filter(marca =>
    normalizar(marca.sistema_tipo) === sistemaTipoNormalizado &&
    !recursosOcultos.marcas.has(marca.id)
  );

  // Buscar la marca seleccionada
  const selectedMarca = marcasDelSistema.find(m => m.nombre === formData.marca);

  // Filtrar modelos por sistema tipo, marca seleccionada y tipo de equipo (excluyendo ocultos)
  const tipoEquipoActual = formData.tipo_equipo && formData.tipo_equipo !== 'nuevo' ? formData.tipo_equipo : null;
  const modelosBase = modelosEquipo.filter(modelo =>
    normalizar(modelo.sistema_tipo) === sistemaTipoNormalizado &&
    !recursosOcultos.modelos.has(modelo.id) &&
    (!selectedMarca || modelo.marca_id === selectedMarca.id)
  );
  const modelosPorTipo = tipoEquipoActual
    ? modelosBase.filter(m => m.tipo_equipo && normalizar(m.tipo_equipo) === normalizar(tipoEquipoActual))
    : modelosBase;
  // Fallback: si no hay modelos asociados al tipo (p.ej. tipo renombrado), mostramos todos los de la marca/sistema.
  const modelosDelSistema = modelosPorTipo.length > 0 ? modelosPorTipo : modelosBase;

  const elementosFiltrados = elementosDelSistema.filter(el => {
    if (filtroTipo && el.tipo !== filtroTipo) return false;
    if (filtroEstado && el.estado !== filtroEstado) return false;
    if (filtroAnomalia) {
      const tieneAnomalia = anomaliasElementos.some(
        a => a.elemento_id === el.id && a.codigo_anomalia === filtroAnomalia
      );
      if (!tieneAnomalia) return false;
    }
    if (filtroMarca && el.marca !== filtroMarca) return false;
    if (filtroModelo && el.modelo !== filtroModelo) return false;
    if (filtroZona && el.zona !== filtroZona) return false;
    if (busquedaGlobal) {
      const term = busquedaGlobal.toLowerCase();
      const searchFields = [
        el.nombre,
        el.tipo,
        el.zona,
        el.marca,
        el.modelo,
        el.mac_serie,
        el.anio_fabricacion,
        el.observaciones,
        el.estado,
        (el as any).numero_elemento != null ? String((el as any).numero_elemento) : '',
        el.fecha_instalacion,
      ];
      const nativeMatch = searchFields.some(v => String(v || '').toLowerCase().includes(term));
      if (!nativeMatch) {
        // Buscar también en datos técnicos
        const dt = el.datos_tecnicos || {};
        const dtMatch = Object.values(dt).some(v => String(v || '').toLowerCase().includes(term));
        if (!dtMatch) return false;
      }
    }
    return true;
  });

  // Extraer valor raw de un elemento según columna de ordenación
  const getSortValue = (el: any, colId: string): string | number => {
    switch (colId) {
      case 'nombre': return String(el.nombre ?? '').toLowerCase();
      case 'tipo': return String(el.tipo ?? '').toLowerCase();
      case 'zona': return String(el.zona ?? '').toLowerCase();
      case 'marca_modelo': return `${String(el.marca ?? '').toLowerCase()} ${String(el.modelo ?? '').toLowerCase()}`;
      case 'mac_serie': return String(el.mac_serie ?? '').toLowerCase();
      case 'anio_fabricacion': {
        const v = String(el.anio_fabricacion ?? '');
        const m = v.match(/(\d{4})/);
        return m ? parseInt(m[1], 10) : v.toLowerCase();
      }
      case 'fecha_instalacion': return String(el.fecha_instalacion ?? '');
      case 'observaciones': return String(el.observaciones ?? '').toLowerCase();
      case 'estado': return String(el.estado ?? '').toLowerCase();
      default:
        if (colId.startsWith('dt:')) {
          const key = colId.slice(3);
          const v = el?.datos_tecnicos?.[key];
          if (v === undefined || v === null) return '';
          if (typeof v === 'number') return v;
          return String(v).toLowerCase();
        }
        return '';
    }
  };

  const elementosOrdenados = useMemo(() => {
    const cols = [...elementosFiltrados];
    cols.sort((a, b) => {
      const av = getSortValue(a, sortColumn);
      const bv = getSortValue(b, sortColumn);
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDirection === 'asc' ? av - bv : bv - av;
      }
      const cmp = String(av).localeCompare(String(bv), 'es', { sensitivity: 'base' });
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return cols;
  }, [elementosFiltrados, sortColumn, sortDirection]);

  // Conjunto de columnas permitidas en este sistema:
  //  - todas las nativas (no dependen del tipo de equipo)
  //  - sólo las columnas técnicas (`dt:<key>`) cuyos campos pertenecen
  //    a algún tipo de equipo realmente presente en la tabla.
  const allowedColumnIds = useMemo(() => {
    const allowed = new Set<string>();
    for (const col of ELEMENTO_COLUMNS) {
      if (!col.id.startsWith('dt:')) allowed.add(col.id);
    }
    const tipos = new Set(elementosDelSistema.map((e) => e.tipo).filter(Boolean) as string[]);
    for (const tipo of tipos) {
      for (const f of getFieldsForTipo(tipo)) {
        allowed.add(`dt:${f.key}`);
      }
    }
    return allowed;
  }, [elementosDelSistema]);

  const { visibleColumns } = useElementoColumnPrefs({ allowedIds: allowedColumnIds });

  // Carga inicial de anomalías al cambiar de sistema
  useEffect(() => {
    cargarAnomaliasElementos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sistemaActual?.id]);

  // Anomalías disponibles en los elementos de este sistema
  const anomaliasDisponibles = useMemo(() => {
    const map = new Map<string, string>();
    for (const el of elementosDelSistema) {
      for (const a of anomaliasElementos) {
        if (a.elemento_id === el.id && a.codigo_anomalia) {
          map.set(a.codigo_anomalia, a.codigo_info?.descripcion || a.codigo_anomalia);
        }
      }
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [elementosDelSistema, anomaliasElementos]);

  const handleResolverAnomaliaInventario = async (elementoId: string, anomaliaId: string) => {
    await resolverAnomalia(anomaliaId);
    const refrescadas = await cargarAnomaliasElementos();
    const quedanActivas = (refrescadas || []).some(
      (a) => a.elemento_id === elementoId && !a.resuelta,
    );
    const el = elementosDelSistema.find((e) => e.id === elementoId);
    if (!quedanActivas && el?.estado === 'anomalia') {
      await editarElemento(elementoId, { estado: 'activo' });
    }
  };

  useEffect(() => {
    if (!sistemaActual) {
      navigate('/clientes');
    }
  }, [sistemaActual, navigate]);


  // Función para filtrar tipos de equipo por sistema actual
  const esMismoSistema = (tipoSistema: string) => {
    return normalizar(tipoSistema) === sistemaTipoNormalizado;
  };
  
  const getColorEstado = (estado: string) => {
    switch (estado.toLowerCase()) {
      case 'activo': return 'bg-green-500';
      case 'inactivo': return 'bg-red-500';
      case 'mantenimiento': return 'bg-yellow-500';
      case 'averiado': return 'bg-red-600';
      default: return 'bg-gray-500';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!sistemaActual || !formData.tipo_equipo || !formData.ubicacion) {
      toast({
        title: "Error",
        description: "Por favor completa el tipo de equipo y la ubicación.",
        variant: "destructive"
      });
      return;
    }

    // Auto-generar nombre como: Tipo - Ubicación
    const nombreFinal = `${formData.tipo_equipo} - ${formData.ubicacion}`;
    
    // Validar formato año fabricación si se ha rellenado
    if (formData.anio_fabricacion && !/^\d{4}$|^\d{2}\/\d{4}$/.test(formData.anio_fabricacion)) {
      toast({
        title: "Formato incorrecto",
        description: "El año de fabricación debe ser AAAA o MM/AAAA (ej: 2024 o 03/2024).",
        variant: "destructive"
      });
      return;
    }

    // Recalcular derivados antes de guardar
    const fields = getFieldsForTipo(formData.tipo_equipo);
    const datosTecnicos = applyDerived(fields, formData.datos_tecnicos || {});

    const elementoData: any = {
      nombre: nombreFinal,
      tipo: formData.tipo_equipo,
      zona: formData.ubicacion || null,
      fecha_instalacion: formData.fecha_instalacion || null,
      marca: formData.marca || null,
      modelo: formData.modelo || null,
      mac_serie: formData.mac_serie || null,
      anio_fabricacion: formData.anio_fabricacion || null,
      observaciones: formData.observaciones || '',
      estado: elementoEditando ? formData.estado : 'activo',
      sistema_id: sistemaActual.id,
      revisado: false,
      datos_tecnicos: datosTecnicos,
    };

    if (elementoEditando) {
      await editarElemento(elementoEditando, elementoData);
    } else {
      await crearElemento(elementoData);
    }

    setDialogAbierto(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      tipo_equipo: '',
      ubicacion: '',
      fecha_instalacion: '',
      marca: '',
      modelo: '',
      mac_serie: '',
      anio_fabricacion: '',
      observaciones: '',
      estado: 'activo',
      datos_tecnicos: {},
    });
    setElementoEditando(null);
    setNuevoTipoEquipo('');
  };

  const abrirEdicion = (elemento: any) => {
    setFormData({
      tipo_equipo: elemento.tipo || '',
      ubicacion: (elemento as any).zona || '',
      fecha_instalacion: elemento.fecha_instalacion || '',
      marca: elemento.marca || '',
      modelo: elemento.modelo || '',
      mac_serie: elemento.mac_serie || '',
      anio_fabricacion: elemento.anio_fabricacion || '',
      observaciones: elemento.observaciones || '',
      estado: elemento.estado || 'activo',
      datos_tecnicos: (elemento.datos_tecnicos as Record<string, any>) || {},
    });
    setElementoEditando(elemento.id);
    setDialogAbierto(true);
  };

  const handleEliminar = async (id: string) => {
    await eliminarElemento(id);
  };

  const handleDuplicar = async (elemento: any) => {
    if (!sistemaActual) return;

    // Generar nombre único para el duplicado
    const nombreBase = `Copia de ${elemento.nombre}`;
    let nombreFinal = nombreBase;
    let contador = 1;

    // Verificar si ya existe un elemento con ese nombre
    while (elementosDelSistema.some(el => el.nombre === nombreFinal)) {
      contador++;
      nombreFinal = `Copia ${contador} de ${elemento.nombre}`;
    }

    const elementoDuplicado: any = {
      nombre: nombreFinal,
      tipo: elemento.tipo,
      zona: (elemento as any).zona || '',
      fecha_instalacion: elemento.fecha_instalacion || null,
      marca: elemento.marca || null,
      modelo: elemento.modelo || null,
      estado: elemento.estado,
      observaciones: elemento.observaciones || '',
      revisado: false,
      sistema_id: sistemaActual.id,
      datos_tecnicos: (elemento as any).datos_tecnicos || {},
    };

    await crearElemento(elementoDuplicado);
    
    toast({
      title: "Elemento duplicado",
      description: `Se ha creado "${nombreFinal}" exitosamente.`,
    });
  };


  const volverASistemas = () => {
    navigate('/sistemas-cliente');
  };

  if (!sistemaActual) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">No hay sistema seleccionado</h2>
          <p className="text-muted-foreground mb-4">Selecciona un sistema para ver sus elementos</p>
          <Button onClick={() => navigate('/sistemas-cliente')}>
            Volver a Sistemas
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4 md:p-6">
      <div className="max-w-6xl mx-auto mobile-spacing">
        {/* Header */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <BackButton onClick={volverASistemas} label="Volver a Sistemas" />
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground mobile-text">
                Elementos - {sistemaActual.tipo}
              </h1>
              <p className="text-muted-foreground text-sm md:text-base">
                {clienteActual?.nombre} - {sistemaActual.ubicacion}
              </p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
            <Button
              variant="secondary"
              onClick={() => setCreacionMasivaAbierta(true)}
              className="flex items-center justify-center gap-2 mobile-button"
            >
              <Layers className="h-4 w-4" />
              Creación masiva
            </Button>

            <CreacionMasivaElementosDialog
              open={creacionMasivaAbierta}
              onOpenChange={setCreacionMasivaAbierta}
              sistemaId={sistemaActual.id}
              sistemaTipo={sistemaActual.tipo}
            />

            <Dialog open={dialogAbierto} onOpenChange={(open) => {
              if (!open && (qrDialogAbierto || qrJustClosedRef.current)) return;
              setDialogAbierto(open);
            }}>
              <DialogTrigger asChild>
                <Button 
                  variant="default"
                  onClick={resetForm}
                  className="flex items-center justify-center gap-2 mobile-button"
                >
                  <Plus className="h-4 w-4" />
                  Nuevo Elemento
                </Button>
              </DialogTrigger>
              
              <DialogContent className="sm:max-w-[720px] mx-4 max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {elementoEditando ? 'Editar Elemento' : 'Crear Nuevo Elemento'}
                  </DialogTitle>
                  <DialogDescription>
                    {elementoEditando 
                      ? 'Modifica los datos del elemento seleccionado.'
                      : 'Completa los datos para crear un nuevo elemento del sistema.'
                    }
                  </DialogDescription>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Tipo de equipo */}
                  <div className="space-y-2">
                    <Label htmlFor="tipo_equipo">Tipo de equipo *</Label>
                    <div className="flex gap-2">
                      <Select 
                        value={formData.tipo_equipo} 
                        onValueChange={(value) => {
                          if (value === 'nuevo') {
                            setNuevoTipoEquipo('');
                            setFormData({ ...formData, tipo_equipo: 'nuevo', datos_tecnicos: {} });
                          } else {
                            setFormData({...formData, tipo_equipo: value, datos_tecnicos: {} });
                          }
                        }}
                      >

                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Seleccionar tipo..." />
                        </SelectTrigger>
                        <SelectContent className="bg-popover border border-border z-50">
                          {tiposEquipo
                            .filter(tipo => esMismoSistema(tipo.sistema_tipo || ''))
                            .filter(tipo => !recursosOcultos.tipos.has(tipo.id))
                            .map((tipo) => (
                              <SelectItem key={tipo.id} value={tipo.nombre}>
                                {tipo.nombre}
                              </SelectItem>
                            ))}
                          <SelectItem value="nuevo">+ Añadir nuevo tipo</SelectItem>
                        </SelectContent>
                      </Select>
                      {formData.tipo_equipo === 'nuevo' && (
                        <div className="flex gap-2 flex-1">
                          <Input
                            placeholder="Nuevo tipo..."
                            value={nuevoTipoEquipo}
                            onChange={(e) => setNuevoTipoEquipo(e.target.value)}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              if (nuevoTipoEquipo.trim()) {
                                await crearTipoEquipo(nuevoTipoEquipo.trim(), sistemaActual.tipo);
                                setFormData({...formData, tipo_equipo: nuevoTipoEquipo.trim()});
                                setNuevoTipoEquipo('');
                              }
                            }}
                          >
                            +
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Ubicación */}
                  <div className="space-y-2">
                    <Label htmlFor="ubicacion">Ubicación *</Label>
                    <Input
                      id="ubicacion"
                      type="text"
                      placeholder="Ej: Entrada principal, Ventana salón, etc."
                      value={formData.ubicacion}
                      onChange={(e) => setFormData({...formData, ubicacion: e.target.value})}
                    />
                  </div>

                  {/* Fecha de instalación */}
                  <div className="space-y-2">
                    <Label htmlFor="fecha_instalacion">Fecha de instalación</Label>
                    <Input
                      id="fecha_instalacion"
                      type="date"
                      value={formData.fecha_instalacion}
                      onChange={(e) => setFormData({ ...formData, fecha_instalacion: e.target.value })}
                    />
                  </div>

                  {/* Marca y Modelo */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="marca">Marca</Label>
                      <SearchableCombobox
                        value={formData.marca}
                        placeholder="Selecciona marca..."
                        options={marcasDelSistema.map((m) => ({ value: m.nombre, label: m.nombre }))}
                        onChange={(value) => setFormData({ ...formData, marca: value, modelo: '' })}
                        onCreate={async (nombre) => {
                          if (!sistemaActual) return;
                          await crearMarcaEquipo(nombre, sistemaActual.tipo);
                          setFormData({ ...formData, marca: nombre, modelo: '' });
                        }}
                        createLabel={(s) => `+ Añadir marca "${s}"`}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="modelo">Modelo</Label>
                      <SearchableCombobox
                        value={formData.modelo}
                        placeholder="Selecciona modelo..."
                        disabled={!selectedMarca}
                        options={modelosDelSistema.map((md) => ({ value: md.nombre, label: md.nombre }))}
                        onChange={(value) => setFormData({ ...formData, modelo: value })}
                        onCreate={async (nombre) => {
                          if (!sistemaActual || !selectedMarca) return;
                          const tipoEq = formData.tipo_equipo && formData.tipo_equipo !== 'nuevo' ? formData.tipo_equipo : undefined;
                          await crearModeloEquipo(nombre, selectedMarca.id, sistemaActual.tipo, tipoEq);
                          setFormData({ ...formData, modelo: nombre });
                        }}
                        createLabel={(s) => `+ Añadir modelo "${s}"`}
                      />
                    </div>
                  </div>


                  {/* Nº Serie / MAC */}
                  <div className="space-y-2">
                    <Label htmlFor="mac_serie">Nº Serie / MAC</Label>
                    <div className="flex gap-2">
                      <Input
                        id="mac_serie"
                        placeholder="Nº de serie, MAC, etc."
                        value={formData.mac_serie}
                        onChange={(e) => setFormData({ ...formData, mac_serie: e.target.value })}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setQrDialogAbierto(true)}
                        className="min-w-[44px] min-h-[44px] shrink-0"
                        title="Escanear código QR"
                      >
                        <ScanLine className="h-5 w-5" />
                      </Button>
                    </div>
                  </div>

                  {/* Año de fabricación */}
                  <div className="space-y-2">
                    <Label htmlFor="anio_fabricacion">Año de fabricación</Label>
                    <Input
                      id="anio_fabricacion"
                      placeholder="AAAA o MM/AAAA (ej: 2024 o 03/2024)"
                      value={formData.anio_fabricacion}
                      onChange={(e) => setFormData({ ...formData, anio_fabricacion: e.target.value })}
                    />
                  </div>


                  {/* Datos técnicos específicos del tipo */}
                  <DatosTecnicosFields
                    tipoEquipo={formData.tipo_equipo && formData.tipo_equipo !== 'nuevo' ? formData.tipo_equipo : undefined}
                    fields={getFieldsForTipo(formData.tipo_equipo)}
                    values={formData.datos_tecnicos || {}}
                    onChange={(next) => setFormData({ ...formData, datos_tecnicos: next })}
                  />


                  {/* Observaciones */}
                  <div className="space-y-2">
                    <Label htmlFor="observaciones">Observaciones</Label>
                    <Textarea
                      id="observaciones"
                      placeholder="Observaciones adicionales..."
                      value={formData.observaciones}
                      onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                      rows={3}
                    />
                  </div>

                  {/* Estado */}
                  <div className="space-y-2">
                    <Label htmlFor="estado">Estado</Label>
                    <Select 
                      value={formData.estado} 
                      onValueChange={(value) => setFormData({...formData, estado: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="activo">Activo</SelectItem>
                        <SelectItem value="inactivo">Inactivo</SelectItem>
                        <SelectItem value="mantenimiento">Mantenimiento</SelectItem>
                        <SelectItem value="averiado">Averiado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setDialogAbierto(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" disabled={isLoading}>
                      {elementoEditando ? 'Actualizar' : 'Crear'} Elemento
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            <QRScannerDialog
              open={qrDialogAbierto}
              onOpenChange={(open) => {
                setQrDialogAbierto(open);
                if (!open) {
                  // Keep ref true for 500ms to protect parent dialog from closing
                  qrJustClosedRef.current = true;
                  setTimeout(() => { qrJustClosedRef.current = false; }, 500);
                }
              }}
              onScanSuccess={(decodedText) => {
                const parsed = parseExtintorQR(decodedText);
                
                const camposRellenados: string[] = [];
                const camposNoMapeados: string[] = [];
                
                const updates: Partial<typeof formData> = {};

                // Serie → mac_serie (text input, direct)
                if (parsed.serie) {
                  updates.mac_serie = parsed.serie;
                  camposRellenados.push(`Nº Serie: ${parsed.serie}`);
                }

                // Año → anio_fabricacion (text input, direct)
                if (parsed.anio) {
                  updates.anio_fabricacion = parsed.anio;
                  camposRellenados.push(`Año fab.: ${parsed.anio}`);
                }

                // Marca → search catalog by name match
                if (parsed.marca) {
                  const marcaMatch = marcasDelSistema.find(m => 
                    normalizar(m.nombre) === normalizar(parsed.marca!)
                  );
                  if (marcaMatch) {
                    updates.marca = marcaMatch.nombre;
                    camposRellenados.push(`Marca: ${marcaMatch.nombre}`);
                  } else {
                    camposNoMapeados.push(`Marca "${parsed.marca}" no encontrada en catálogo`);
                  }
                }

                // Modelo → search catalog by name match
                if (parsed.modelo) {
                  const modeloMatch = modelosDelSistema.find(md => 
                    normalizar(md.nombre) === normalizar(parsed.modelo!)
                  );
                  if (modeloMatch) {
                    updates.modelo = modeloMatch.nombre;
                    camposRellenados.push(`Modelo: ${modeloMatch.nombre}`);
                  } else {
                    camposNoMapeados.push(`Modelo "${parsed.modelo}" no encontrado en catálogo`);
                  }
                }

                // If unmapped fields, add raw QR data to observations
                if (camposNoMapeados.length > 0) {
                  const obsExtra = `[QR: ${parsed.raw}]`;
                  updates.observaciones = formData.observaciones 
                    ? `${formData.observaciones}\n${obsExtra}` 
                    : obsExtra;
                }

                setFormData(prev => ({ ...prev, ...updates }));

                // Toast with detailed info
                const desc = camposRellenados.length > 0
                  ? camposRellenados.join(' | ')
                  : 'No se pudieron mapear campos automáticamente';
                const fullDesc = camposNoMapeados.length > 0
                  ? `${desc}\n⚠️ ${camposNoMapeados.join(', ')}`
                  : desc;
                
                toast({
                  title: "Código escaneado",
                  description: fullDesc,
                });
              }}
            />
          </div>
        </div>

        {/* Acciones de revisión (mismos datos que la tarjeta del sistema) */}
        {sistemaActual && clienteActual && (
          <SistemaRevisionActions
            sistemaId={sistemaActual.id}
            sistemaTipo={sistemaActual.tipo}
            clienteId={clienteActual.id}
            clienteNombre={clienteActual.nombre}
          />
        )}

        {/* Filtros */}


            {/* Filtros */}
            <div className="bg-card border border-border rounded-lg shadow-sm">
              <div className="p-4 border-b">
                {/* Búsqueda global */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar en cualquier propiedad (nombre, tipo, ubicación, marca, modelo, serie, año, observaciones, datos técnicos...)"
                    value={busquedaGlobal}
                    onChange={(e) => setBusquedaGlobal(e.target.value)}
                    className="pl-10"
                  />
                  {busquedaGlobal && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setBusquedaGlobal('')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="flex flex-col gap-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Tipo</Label>
                      <Select
                        value={filtroTipo || "all"}
                        onValueChange={(v) => setFiltroTipo(v === "all" ? "" : v)}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Todos" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          {tiposDisponibles.map((tipo) => (
                            <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Estado</Label>
                      <Select
                        value={filtroEstado || "all"}
                        onValueChange={(v) => setFiltroEstado(v === "all" ? "" : v)}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Todos" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          {estadosDisponibles.map((e) => (
                            <SelectItem key={e} value={e} className="capitalize">{e}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {marcasDisponibles.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Marca</Label>
                        <Select
                          value={filtroMarca || "all"}
                          onValueChange={(v) => setFiltroMarca(v === "all" ? "" : v)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Todas" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todas</SelectItem>
                            {marcasDisponibles.map((m) => (
                              <SelectItem key={m} value={m}>{m}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {modelosDisponibles.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Modelo</Label>
                        <Select
                          value={filtroModelo || "all"}
                          onValueChange={(v) => setFiltroModelo(v === "all" ? "" : v)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Todos" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            {modelosDisponibles.map((m) => (
                              <SelectItem key={m} value={m}>{m}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {zonasDisponibles.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Ubicación</Label>
                        <Select
                          value={filtroZona || "all"}
                          onValueChange={(v) => setFiltroZona(v === "all" ? "" : v)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Todas" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todas</SelectItem>
                            {zonasDisponibles.map((z) => (
                              <SelectItem key={z} value={z}>{z}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {anomaliasDisponibles.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Anomalía</Label>
                        <Select
                          value={filtroAnomalia || "all"}
                          onValueChange={(v) => setFiltroAnomalia(v === "all" ? "" : v)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Todas" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todas</SelectItem>
                            {anomaliasDisponibles.map(([codigo, descripcion]) => (
                              <SelectItem key={codigo} value={codigo}>{codigo} — {descripcion}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex items-center gap-1">
                        <Select
                          value={sortColumn}
                          onValueChange={(v) => setSortColumn(v)}
                        >
                          <SelectTrigger className="w-[180px] h-9">
                            <SelectValue placeholder="Ordenar por..." />
                          </SelectTrigger>
                          <SelectContent>
                            {ELEMENTO_COLUMNS.filter(c => allowedColumnIds.has(c.id)).map((col) => (
                              <SelectItem key={col.id} value={col.id}>{col.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-9 w-9 p-0"
                          onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                          title={sortDirection === 'asc' ? 'Ascendente' : 'Descendente'}
                        >
                          {sortDirection === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
                        </Button>
                      </div>
                      <ColumnasElementosPicker allowedIds={allowedColumnIds} />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setFiltroTipo('');
                        setFiltroEstado('');
                        setFiltroAnomalia('');
                        setBusquedaGlobal('');
                        setFiltroMarca('');
                        setFiltroModelo('');
                        setFiltroZona('');
                      }}
                    >
                      Limpiar filtros
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Tabla de elementos */}
              <div className="overflow-x-auto">
                {elementosOrdenados.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="flex flex-col items-center gap-4">
                      <Monitor className="h-12 w-12 text-muted-foreground" />
                      <h3 className="text-lg font-medium text-foreground">
                        {elementosDelSistema.length === 0 
                          ? 'No hay elementos en este sistema' 
                          : 'No se encontraron elementos con los filtros aplicados'
                        }
                      </h3>
                      <p className="text-muted-foreground max-w-md">
                        {elementosDelSistema.length === 0
                          ? 'Comienza agregando el primer elemento a este sistema de seguridad.'
                          : 'Intenta ajustar los filtros o limpiarlos para ver más elementos.'
                        }
                      </p>
                    </div>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead className="w-12 text-center" title="Anomalías activas">!</TableHead>
                        {visibleColumns.map((col) => (
                          <TableHead key={col.id}>{col.label}</TableHead>
                        ))}
                        <TableHead className="w-40 text-right">Acciones</TableHead>
                      </TableRow>

                    </TableHeader>
                    <TableBody>
                      {elementosOrdenados.map((elemento) => {
                        const expandida = filasExpandidas.has(elemento.id);
                        const toggleExpand = () => {
                          setFilasExpandidas((prev) => {
                            const next = new Set(prev);
                            if (next.has(elemento.id)) next.delete(elemento.id);
                            else next.add(elemento.id);
                            return next;
                          });
                        };
                        return (
                          <React.Fragment key={elemento.id}>
                            <TableRow
                              className="hover:bg-muted/50 transition-colors"
                            >
                              <TableCell>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0"
                                  onClick={toggleExpand}
                                  aria-label={expandida ? 'Ocultar detalle' : 'Ver detalle'}
                                >
                                  {expandida ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </Button>
                              </TableCell>
                              <TableCell className="text-center">
                                <AnomaliasIndicator
                                  anomalias={anomaliasElementos.filter((a) => a.elemento_id === elemento.id)}
                                  onResolver={(anomaliaId) => handleResolverAnomaliaInventario(elemento.id, anomaliaId)}
                                  size="sm"
                                />
                              </TableCell>
                              {visibleColumns.map((col) => (

                                <TableCell key={col.id} className={col.className}>
                                  {col.render(elemento)}
                                </TableCell>
                              ))}
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <ElementoFotosButton elementoId={elemento.id} elementoNombre={elemento.nombre} />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => abrirEdicion(elemento)}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>

                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDuplicar(elemento)}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Copy className="h-4 w-4" />
                                  </Button>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>¿Eliminar elemento?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Esta acción no se puede deshacer. Se eliminará permanentemente el elemento "{elemento.nombre}".
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={() => handleEliminar(elemento.id)}
                                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                        >
                                          Eliminar
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </div>
                              </TableCell>
                            </TableRow>
                            {expandida && (
                              <ElementoDetalleFila elemento={elemento} colSpan={visibleColumns.length + 3} />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>



      </div>
    </div>
  );
};

export default ElementosSistema;